import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { useClient } from '@/entities/client/model/queries'
import { useSite } from '@/entities/site/model/queries'
import { usePermissions } from '@/entities/session/hooks/usePermissions'
import { SiteMemoBookView } from '@/features/memo-logbook'
import { memoLogbookSearchSchema } from '@/features/memo-logbook/useMemoLogbookFilters'
import { PageHeader } from '@/shared/components/layout/PageHeader'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { PERMISSIONS } from '@/shared/config/permissions'

export const Route = createFileRoute('/clients/$clientId/sites/$siteId/memo-book')({
  component: SiteMemoBookPage,
  staticData: { authorize: true },
  validateSearch: z.object(memoLogbookSearchSchema),
})

function SiteMemoBookPage() {
  const { clientId, siteId } = Route.useParams()
  const clientIdNum = parseInt(clientId)
  const siteIdNum = parseInt(siteId)
  const { hasPermission, isLoading: loadingPermissions } = usePermissions()
  const { data: client } = useClient(clientIdNum)
  const { data: site } = useSite(siteIdNum)

  const clientName = client?.name || 'Client'
  const siteName = site?.siteName || 'Site'

  const header = (
    <PageHeader
      title="Memo book"
      description={site?.siteName}
      breadcrumbs={[
        { label: 'Clients', href: '/clients' },
        { label: clientName, href: `/clients/${clientId}` },
        { label: siteName, href: `/clients/${clientId}/sites/${siteId}` },
        { label: 'Memo book' },
      ]}
    />
  )

  if (loadingPermissions) {
    return (
      <>
        {header}
        <Skeleton className="h-40 w-full" />
      </>
    )
  }

  if (!hasPermission(PERMISSIONS.MemoBookView)) {
    return (
      <>
        {header}
        <p className="text-sm text-muted-foreground">You don't have permission to view the memo book.</p>
      </>
    )
  }

  return (
    <div className="space-y-6">
      {header}
      <SiteMemoBookView siteId={siteIdNum} siteName={siteName} />
    </div>
  )
}
